/**
 * Criterion summary card for evaluation detail panes.
 */
import NewCross from "@/svg/new-cross";
import Tick from "@/svg/Tick";
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import type { CSSProperties } from "react";
import { getCriteriaCardStatusStyles } from "./criteriaCardStatusStyles";
import { ActionPending } from "@/svg/action-pending";

/**
 * Card corner that can receive a border radius.
 */
export type CriteriaCardCorner =
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";

/**
 * Card edge that can receive a border.
 */
export type CriteriaCardBorderSide = "top" | "right" | "bottom" | "left";

const ROUNDED_CORNER_CLASS: Record<CriteriaCardCorner, string> = {
    "top-left": "rounded-tl-lg",
    "top-right": "rounded-tr-lg",
    "bottom-left": "rounded-bl-lg",
    "bottom-right": "rounded-br-lg",
};

const BORDER_SIDE_CLASS: Record<CriteriaCardBorderSide, string> = {
    top: "border-t",
    right: "border-r",
    bottom: "border-b",
    left: "border-l",
};

/**
 * Props for the criterion summary card in the evaluation detail pane.
 */
export interface CriteriaCardProps {
    id: string;
    indexLabel: string;
    statement: string;
    finding: string;
    roundedCorners?: CriteriaCardCorner[];
    borders?: CriteriaCardBorderSide[];
    leftBorderColor?: string;
    leftBorderWidth?: number;
    /** Screening status used to pick the header icon and DATA_MISSING border. */
    screeningStatus?: string;
    /** Whether View Evidence can open the evidence modal (PDF or text-only evidence). */
    hasEvidence?: boolean;
    /** CRC override verdict when one applies to this atom. */
    overrideVerdict?: string | null;
    /** CRC override comment, shown in its own row when set. */
    overrideComment?: string | null;
    /** Opens the manual override modal for this atom. */
    onManualOverrideClick?: () => void;
    /** Opens the View Evidence modal for this atom. */
    onViewEvidenceClick?: () => void;
}

/**
 * Builds Tailwind radius classes for the requested corners.
 *
 * @param corners - Corners to round; all four when omitted, none when empty
 * @returns Radius utility classes
 */
const getRoundedCornerClasses = (corners?: CriteriaCardCorner[]): string => {
    if (corners === undefined) {
        return "rounded-lg";
    }

    return corners.map((corner) => ROUNDED_CORNER_CLASS[corner]).join(" ");
};

/**
 * Builds Tailwind border-side classes for the requested edges.
 *
 * @param sides - Sides to border; all four when omitted, none when empty
 * @returns Border utility classes
 */
const getBorderSideClasses = (sides?: CriteriaCardBorderSide[]): string => {
    if (sides === undefined) {
        return "border border-primaryGray-14";
    }

    if (sides.length === 0) {
        return "";
    }

    const sideClasses = sides.map((side) => BORDER_SIDE_CLASS[side]).join(" ");
    return `${sideClasses} border-primaryGray-14`.trim();
};

/**
 * Whether the left edge is part of the painted border.
 *
 * @param sides - Requested border sides; all sides when omitted
 * @returns True when the left edge is included
 */
const hasLeftBorder = (sides?: CriteriaCardBorderSide[]): boolean => {
    return sides === undefined || sides.includes("left");
};

/**
 * Inline left-border overrides; omitted props keep 1px primaryGray-14.
 *
 * @param sides - Requested border sides
 * @param leftBorderColor - Explicit left color, if any
 * @param leftBorderWidth - Explicit left width in px, if any
 * @returns Style object, or undefined when defaults apply
 */
const getLeftBorderStyle = (
    sides: CriteriaCardBorderSide[] | undefined,
    leftBorderColor: string | undefined,
    leftBorderWidth: number | undefined,
): CSSProperties | undefined => {
    if (!hasLeftBorder(sides)) {
        return undefined;
    }

    const style: CSSProperties = {};
    if (leftBorderColor !== undefined) {
        style.borderLeftColor = leftBorderColor;
    }
    if (leftBorderWidth !== undefined && leftBorderWidth !== 1) {
        style.borderLeftWidth = leftBorderWidth;
    }

    return Object.keys(style).length > 0 ? style : undefined;
};

/**
 * Criterion card with statement, findings, and header actions.
 *
 * @param props - Card chrome
 * @param props.id - Unique id for the criterion card
 * @param props.indexLabel - Progress tag shown in the header (e.g. "1/3")
 * @param props.statement - Criterion statement body
 * @param props.finding - Findings text shown in the highlighted row
 * @param props.roundedCorners - Which corners are rounded; all corners when omitted
 * @param props.borders - Which sides are bordered; all sides when omitted
 * @param props.leftBorderColor - Left-edge color; primaryGray-14 when omitted
 * @param props.leftBorderWidth - Left-edge width in px; 1 when omitted
 * @param props.screeningStatus - Screening status used to pick the header icon and DATA_MISSING border
 * @param props.hasEvidence - When true, View Evidence is enabled (PDF or text evidence)
 * @param props.overrideVerdict - CRC override verdict when one applies to this atom
 * @param props.overrideComment - CRC override comment in a separate row
 * @param props.onManualOverrideClick - Opens the manual override modal
 * @param props.onViewEvidenceClick - Opens the View Evidence modal
 * @returns Criterion summary card
 */
const CriteriaCard = ({
    indexLabel,
    statement,
    finding,
    roundedCorners,
    borders,
    leftBorderColor,
    leftBorderWidth,
    screeningStatus,
    hasEvidence = false,
    overrideVerdict,
    overrideComment,
    onManualOverrideClick,
    onViewEvidenceClick,
}: CriteriaCardProps) => {
    const statusIcon = (screeningStatus: string | undefined): React.ReactNode => {
    switch (screeningStatus) {
        case "PASS":
            return <Tick />;
        case "FAIL":
            return <NewCross />;
        case "DATA_MISSING":
            return <ActionPending />;
        default:
            return null;
    }
    };
    const statusStyles = getCriteriaCardStatusStyles(
        screeningStatus,
        leftBorderColor,
        leftBorderWidth,
    );

    return (
        <div
            className={`criteria-card bg-white flex-1 p-4 flex flex-col gap-4 ${getBorderSideClasses(borders)} ${getRoundedCornerClasses(roundedCorners)}`}
            style={getLeftBorderStyle(
                borders,
                statusStyles.leftBorderColor,
                statusStyles.leftBorderWidth,
            )}
        >
            <div className="criteria-card--header flex gap-3 items-center">
                <div className="index"><Tags label={indexLabel} backgroundColor="#F5F5F5" textColor="#0F0F0F" /></div>
                <div className="text flex-1 text-tiny font-bold text-primaryGray-7">Criteria</div>
                {overrideVerdict && (
                    <Tags
                        label={`CRC override: ${overrideVerdict}`}
                        backgroundColor="#E8F1FF"
                        textColor="#0056D6"
                    />
                )}
                <button
                    type="button"
                    disabled={!hasEvidence}
                    className={`actionables flex items-center justify-center px-2 py-1 border rounded border-primaryGray-14 transition-all ${
                        hasEvidence
                            ? "hover:cursor-pointer hover:bg-primaryGray-16 hover:text-primaryGray-1"
                            : "cursor-not-allowed opacity-50"
                    }`}
                    onClick={onViewEvidenceClick}
                >
                    <span className="clickable-custom text-x-tiny text-primaryGray-3 font-[500]">
                        View Evidence
                    </span>
                </button>
                <button
                    type="button"
                    className="actionables flex items-center justify-center px-2 py-1 border rounded border-primaryGray-14 hover:cursor-pointer hover:bg-primaryGray-16 hover:text-primaryGray-1 transition-all"
                    onClick={onManualOverrideClick}
                >
                    <span className="clickable-custom text-x-tiny text-primaryGray-3 font-[500]">
                        Manually Override
                    </span>
                </button>
                <div className="icon flex items-center justify-center">
                    {statusIcon(screeningStatus)}
                </div>
            </div>
            <div className="criteria-card--content flex flex-col gap-3">
                <div className="content-statement text-tiny font-semibold text-primaryGray-3">{statement}</div>
                <div
                    className={`content-finding flex flex-col gap-2 rounded-md border px-3 py-3 text-tiny ${statusStyles.findingContainerClasses}`}
                >
                    <div className="flex items-start gap-1">
                        <span
                            className={`shrink-0 font-bold ${statusStyles.findingLabelClass}`}
                        >
                            Findings:
                        </span>
                        <span
                            className="min-w-0 flex-1 whitespace-pre-wrap leading-5 text-primaryGray-1"
                        >
                            {finding}
                        </span>
                    </div>
                    {overrideComment?.trim() && (
                        <p className="rounded-md border border-primaryGray-14 bg-white/70 px-2 py-2 text-x-tiny leading-[1.125rem] text-primaryGray-3">
                            <span className="font-semibold text-primaryGray-5">
                                Override comment
                            </span>
                            <span className="mt-0.5 block whitespace-pre-wrap">
                                {overrideComment.trim()}
                            </span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CriteriaCard;
